import { Component, effect, inject, input, OnDestroy, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { forkJoin, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../../error.utils';
import { AssignmentService } from '../../../../service/assignment.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../service/notify.service';
import { UserLoginService } from '../../../../service/user-login.service';
import { IncidentFormContext, ReviewOutcome, ReviewStatus, ReviewSubjectType, SubmitValidationReviewRequest } from '../../../../types';
import { ReviewSubmissionFailedModalComponent } from '../review-submission-failed-modal/review-submission-failed-modal.component';
import { incidentFormValidator } from '../review-submit-form/form-utils';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { parseReviewStatus, parseReviewSubjectType, reviewOutcomeToFormValues } from '../review-utils';
import { AssignmentDetailsStore } from '../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-validation-assignment',
  templateUrl: './validation-assignment.component.html',
  styleUrl: './validation-assignment.component.scss',
  standalone: false,
  providers: [AssignmentDetailsStore],
})
export class ValidationAssignmentComponent implements OnInit, OnDestroy {
  public reviewFormService = inject(ReviewFormService);
  private fb = inject(FormBuilder);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  public readonly assignmentDetailsStore = inject(AssignmentDetailsStore);
  private notifyService = inject(NotifyService);
  private userService = inject(UserLoginService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  public incidentFormRef = signal<TemplateRef<unknown> | undefined>(undefined);
  public validationForm = this.fb.group({
    commentsRating: this.fb.control(0, [Validators.required, Validators.min(1), Validators.max(5)]),
  });
  public initialLocationTimezone = input<string | undefined>();
  public locationTimezone = signal<string | undefined>(undefined);
  public currentValidationId = signal<string | undefined>(undefined);
  public validationId = input<string | undefined>(undefined);
  public assignmentType = signal<ReviewSubjectType | undefined>(undefined);
  public readonly ReviewSubjectType = ReviewSubjectType;
  // shorthand for: investigation assignment id
  public investigationId = signal<string | null>(null);
  public rhombusPasswordOptOut = signal<boolean>(false);
  public isCompleted = signal<boolean>(false);
  public locationId = signal<number | undefined>(undefined);

  public instanceAtDate = signal<Date | undefined>(undefined);

  public submitErrorMessage = signal<string | undefined>(undefined);
  public isSubmitting = signal<boolean>(false);
  private reviewOutcome = signal<ReviewOutcome | undefined>(undefined);

  private submittedSwal = viewChild<SwalComponent>('submittedSwal');
  private failedModal = viewChild<ReviewSubmissionFailedModalComponent>('failedModal');

  public ngOnInit() {
    this.locationTimezone.set(this.initialLocationTimezone());
    this.loadValidation();
  }

  constructor() {
    effect(() => {
      const v = this.validationId();
      if (v && v !== this.currentValidationId()) {
        this.currentValidationId.set(v);
        this.load();
      }
    });
  }

  public onContextReady(ctx: IncidentFormContext) {
    this.instanceAtDate.set(ctx.instanceAtDate);
  }

  public ngOnDestroy(): void {
    this.reviewFormService.resetForm();
    this.reviewFormService.form.setValidators(incidentFormValidator);
  }

  public submitValidation(noChanges: boolean = false) {
    const incidentForm = this.reviewFormService.form.value;
    const findingsForm = this.reviewFormService.findingsForm.value;
    const validationForm = this.validationForm.value;
    this.isSubmitting.set(true);
    this.reviewFormService
      .uploadFiles()
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: ({ uploadedPhotosLinks, uploadedReportsLinks }) => {
          const submitRequest: SubmitValidationReviewRequest = {
            is_theft: incidentForm.isTheft,
            case_id: incidentForm.matchingCase === 'new_case' ? null : incidentForm.matchingCase,
            report_links: uploadedReportsLinks,
            photos_links: uploadedPhotosLinks,
            extra_links: incidentForm.extraLinks.map((l) => ({ link: l })),
            theft_type: {
              is_cancellation: incidentForm.theftType.is_cancellation ?? false,
              is_underringing: incidentForm.theftType.is_underringing ?? false,
              is_walkout: incidentForm.theftType.is_walkout ?? false,
              is_cashier: incidentForm.theftType.is_cashier ?? false,
            },
            suspected_items: incidentForm.suspectedItems?.map((i) => ({ id: i.id!, total: i.total })),
            comments: findingsForm.comments,
            additional_stolen_value: incidentForm.additionalStolenValue,
            instance_at: this.reviewFormService.getValidInstanceAt(incidentForm.instance_at, this.instanceAtDate()),
            recent_walk_in_ids: incidentForm.selectedImages,
            no_changes: noChanges,
            comments_rating: validationForm.commentsRating,
            use_kiosk_photo: incidentForm.useKioskPhoto,
            annotations: this.reviewOutcome()?.keystore_parsed.annotations,
          };

          this.lpService
            .submitValidation(this.currentValidationId(), submitRequest)
            .pipe(finalize(() => this.isSubmitting.set(false)))
            .subscribe(
              () => {
                this.submittedSwal()?.fire();
                this.loadValidation();
              },
              (err) => {
                const errorRequest = {
                  comments: JSON.stringify(err),
                  error_message: err.error?.error ?? '',
                  errors: [{ is_blocking: false, review_error_type: 'Exception' }],
                };
                this.lpService.submitError(this.currentValidationId(), errorRequest).subscribe(
                  () => {},
                  () => {},
                );
                this.submitErrorMessage.set(tryGetErrorMessage(err));
                this.failedModal()?.open();
              },
            );
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.notifyService.notify(`Failed to upload images: ${tryGetErrorMessage(err)}`);
        },
      });
  }

  public onRatingChange(rating: number): void {
    this.validationForm.controls.commentsRating.setValue(rating);
    this.validationForm.controls.commentsRating.markAsTouched();
    this.validationForm.updateValueAndValidity();
  }

  public goToDashboard() {
    const isValidator = this.userService.currentProfileRoleList().includes('validator');
    this.router.navigate([isValidator ? '/lp/validate' : '/lp']);
  }

  private loadValidation(): void {
    const routeParamId = this.route.snapshot.params.assignmentId as string | undefined;
    const fromParent = this.validationId();
    const resolved = fromParent ?? routeParamId;
    if (resolved && resolved !== this.currentValidationId()) {
      this.currentValidationId.set(resolved);
      this.load();
    }
  }

  private load(): void {
    const id = this.currentValidationId();
    if (!id) return;
    this.reviewOutcome.set(undefined);
    this.assignmentService
      .getAssignment(id)
      .pipe(
        switchMap((v) =>
          forkJoin({
            // this contains the original assignment information (i.e. cancellation / sideloaded / walkout)
            validation: of(v),
            // this contains the validation assignment information
            assignment: this.assignmentService.getAssignment(id),
          }),
        ),
      )
      .subscribe(({ validation: v, assignment }) => {
        this.isCompleted.set(parseReviewStatus(assignment.status) === ReviewStatus.Completed);
        this.locationId.set(assignment.location_id);

        if (!this.initialLocationTimezone() && assignment.location_timezone) {
          this.locationTimezone.set(assignment.location_timezone);
        }

        const investigation = v.subject_id;
        this.investigationId.set(investigation);
        if (!investigation) return;
        // validation assignments provide the ability to clear or reset the video clip passwords
        // we need to know if its CLEAR or RESET, depends on the branch setting
        // also populate the store so child incident forms can use it
        this.assignmentService.getAssignment(investigation).subscribe((review) => {
          this.rhombusPasswordOptOut.set(review.rhombus_password_opt_out ?? false);
          this.assignmentDetailsStore.set(review);
          this.assignmentType.set(parseReviewSubjectType(review.subject_type));
        });
        // we need to load the original assignment submission
        this.assignmentService.getReviewOutcomes(investigation).subscribe((outcomes) => {
          if (outcomes && outcomes.length > 0) {
            const last = outcomes[outcomes.length - 1];
            this.reviewOutcome.set(last);
            const v = reviewOutcomeToFormValues(last);
            this.reviewFormService.applyInitialValues(v);
          }
        });
      });
  }
}
